<script setup lang="ts">
import LeCross from "@dsk/icons/LeCross.vue";
import type { ModalProps } from "@dsk/types";
import { Dialog as DialogElement, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from "@headlessui/vue";
import { useEventListener } from "@vueuse/core";
import { computed, ref, useSlots } from "vue";
import { useI18n } from "vue-i18n";

import LeIconButton from "./IconButton/LeIconButton.vue";

type Props = ModalProps & {
  color?: "primary" | "accent" | "error";
  size?: "sm" | "md" | "lg" | "xl";
};

const props = withDefaults(defineProps<Props>(), { color: "primary", size: "sm" });

const emit = defineEmits(["close"]);

defineOptions({
  inheritAttrs: false
});

const { t } = useI18n();
const slots = useSlots();

const onClose = () => emit("close");

const bodyColorClass = computed<string>(() => `le-modal-panel__body--${props.color}`);

// Binding a click event via Vue's @click directive does not work on the backdrop element,
// so we do it in a more old-school way.
const backdropEl = ref<HTMLElement | null>(null);
useEventListener(backdropEl, "click", onClose, { passive: true });
</script>

<template>
  <TransitionRoot :show="props.isOpen" as="template">
    <DialogElement v-bind="$attrs" tabindex="-1" class="le-modal" @keydown.esc.stop.prevent="onClose()">
      <TransitionChild
        as="template"
        enter="fade-enter-active"
        enter-from="fade-enter-from"
        leave="fade-leave-active"
        leave-to="fade-leave-to"
      >
        <div ref="backdropEl" class="le-modal__backdrop" aria-hidden="true" />
      </TransitionChild>

      <TransitionChild
        as="template"
        enter="fade-enter-active"
        enter-from="fade-enter-from"
        leave="fade-leave-active"
        leave-to="fade-leave-to"
      >
        <div class="le-modal__dialog">
          <DialogPanel class="le-modal-panel" :class="`le-modal-panel--${props.size}`">
            <DialogTitle class="le-modal-panel__title" as="header">
              <slot name="title" />
              <LeIconButton :aria-label="t('close-modal')" :color="props.color" @click="onClose()">
                <LeCross role="presentation" />
              </LeIconButton>
            </DialogTitle>

            <section class="le-modal-panel__body" :class="bodyColorClass">
              <slot name="body" />

              <div v-if="slots.buttons" class="le-modal-panel__body-buttons">
                <slot name="buttons" />
              </div>
            </section>
          </DialogPanel>
        </div>
      </TransitionChild>
    </DialogElement>
  </TransitionRoot>
</template>

<style scoped lang="less">
@import "@dsk/styles/all.less";

@modal-z-index: 1040;
@modal-backdrop-z-index: 1;
@modal-dialog-z-index: 2;

// For some reason .le-modal doesn't get a v-scope-id
:global(.le-modal) {
  display: flex;
  position: fixed;
  z-index: @modal-z-index;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  align-items: center;
  justify-content: center;
}

.le-modal {
  &__backdrop {
    position: fixed;
    z-index: @modal-backdrop-z-index;
    inset: 0;
    background: fade(@colors[@neutrals][300], 40%);
  }

  &__dialog {
    z-index: @modal-dialog-z-index;
    box-shadow: 0px 11px 12px -7px rgba(32, 43, 33, 0.25);
  }

  &-panel {
    width: 100vw;
    max-height: 100vh;
    @supports (max-height: 100dvh) {
      max-height: 100dvh;

      #media-breakpoint-up(lg, {
        max-height: 96dvh;
      });
    }
    overflow-y: auto;
    border-radius: 0;

    #media-breakpoint-up(lg, {
      max-height: 96vh;
      border-radius: 4px;
    });

    &--sm {
      max-width: 555px;
    }

    &--md {
      max-width: 606px;
    }

    &--lg {
      max-width: 945px;
    }

    // Does not exist in Figma, but it is used for the "included dictionaries" on the subscription page
    &--xl {
      max-width: 1140px;
    }

    &__title,
    &__body {
      padding: 20px 24px;
    }

    &__title {
      display: flex;
      align-items: center;
      justify-content: space-between;
      height: 75px;
      background: @colors[@neutrals][100];

      :slotted(.le-heading) {
        margin-right: 10px;
      }
    }

    &__body {
      .body-text("m");
      color: @colors[@neutrals][500];

      &--primary {
        background: @colors[@secondary][100];
      }

      &--accent {
        background: @colors[@accent][100];
      }

      &--error {
        background: @colors[@error][100];
      }

      &-buttons {
        display: flex;
        gap: 10px;
        flex-direction: column-reverse;
        min-height: 65px;
        margin-top: 20px;

        #media-breakpoint-up(sm, {
          align-items: center;
          flex-direction: row;
          justify-content: flex-end;
        });
      }
    }
  }
}

// Ensure that images don't overflow
:deep(img),
:deep(svg) {
  max-width: 100%;
}

// Transitions
.fade-enter-active,
.fade-leave-active {
  transition: opacity 75ms ease-out;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>

<i18n lang="json">
{
  "da": {
    "close-modal": "Luk vindue"
  },
  "en": {
    "close-modal": "Close window"
  },
  "de": {
    "close-modal": "Fenster schließen"
  }
}
</i18n>
